import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

@Injectable({
  providedIn: 'root'
})
/**
 * Enhanced Logger Service for Angular frontend
 * 
 * Features:
 * - Environment-aware logging (disabled in production unless dev.cms/localhost)
 * - Multiple log levels (debug, info, warn, error)
 * - Structured logging with context
 * - Performance-optimized (no-op in production)
 * 
 * Usage:
 *   constructor(private logger: LoggerService) {}
 *   this.logger.debug('Debug message', data);
 *   this.logger.info('Info message', data);
 *   this.logger.warn('Warning message', data);
 *   this.logger.error('Error message', error);
 */
export class LoggerService {
  // Check if we are in production environment.
  private readonly isProd: boolean = environment.production;
  // Check for dev env
  private readonly isDev: boolean = false;
  // Current log level (can be adjusted based on environment)
  private readonly currentLogLevel: LogLevel = LogLevel.DEBUG;

  constructor(private router: Router) {
    // Validates if we are on dev/localhost cms
    this.isDev = window.location.href.includes('dev.cms') || window.location.href.includes('localhost');
  }

  /**
   * Check if logging is enabled for the current environment
   */
  private shouldLog(): boolean {
    return !this.isProd || this.isDev;
  }

  /**
   * Write debug message to console (only in dev/localhost)
   * Use for detailed debugging information
   */
  debug(message: string, data: any = null, context?: string): void {
    if (!this.shouldLog() || this.currentLogLevel > LogLevel.DEBUG) {
      return;
    }
    const contextStr = context ? `[${context}]` : '';
    console.debug(`${contextStr} ${message}`, data || '');
  }

  /**
   * Write info message to console (only in dev/localhost)
   * Use for general informational messages
   */
  info(message: string, data: any = null, context?: string): void {
    if (!this.shouldLog() || this.currentLogLevel > LogLevel.INFO) {
      return;
    }
    const contextStr = context ? `[${context}]` : '';
    console.info(`${contextStr} ${message}`, data || '');
  }

  /**
   * Write warning message to console (only in dev/localhost)
   * Use for warnings that don't break functionality
   */
  warn(message: string, data: any = null, context?: string): void {
    if (!this.shouldLog() || this.currentLogLevel > LogLevel.WARN) {
      return;
    }
    const contextStr = context ? `[${context}]` : '';
    console.warn(`${contextStr} ${message}`, data || '');
  }

  /**
   * Write error message to console (only in dev/localhost)
   * Use for errors and exceptions
   */
  error(message: string, error: any = null, context?: string): void {
    if (!this.shouldLog() || this.currentLogLevel > LogLevel.ERROR) {
      return;
    }
    const contextStr = context ? `[${context}]` : '';
    console.error(`${contextStr} ${message}`, error || '');
  }

  /**
   * Write to console a given message (legacy method - use info() instead)
   * @deprecated Use info(), debug(), warn(), or error() instead
   */
  log(message: string, data: any = null, from: any = '', functionFrom: any = ''): void {
    if (!this.shouldLog()) {
      return;
    }
    const context = from || functionFrom ? `From: ${from} - Function: ${functionFrom}` : '';
    console.log(message, data, context);
  }

  /**
   * Write to console a given object as table form
   *
   * @param message The message to display
   * @param object The object to display
   */
  logObjectAsTable(message: string, object: any): void {
    if (!this.shouldLog()) {
      return;
    }
    console.table(message, object);
  }
}
